import { fetcher, QueryOptions, useQueryFetch } from '@shared/fetch-utils';
import { buildUrl } from '@shared/utils';
import { MgSupplierItemSearchResultDTO } from '@retail/products/types';
import { PaginationResultDTO } from '@shared/fetch-utils';
import { useMgPrisConfig } from '@retail/app/stores/app-config';
import { useAuthContext } from '@retail/auth/data-access';
import { useCallback } from 'react';
import { useMutateMPEFetch, useSelectedOrgUnitHeader } from '@retail/app/hooks';
import { mpeApi } from '@retail/mgpris/config';

export interface SearchOptions {
  query?: string;
  supplierIds?: number[];
  groupNumbers?: string[];
  discountGroupIds?: number[];
  assortmentId?: number;
  withAgreementPrices?: boolean;
  filterDiscontinued?: boolean;
  fromMainSupplier?: boolean;
  includeOwner?: boolean;
  inAssortment?: boolean;
  page: number;
  size: number;
}

interface FetchProps extends SearchOptions {
  baseUrl: string;
  token: string;
  headers?: HeadersInit;
}

export async function searchMgSupplierItems({
  baseUrl,
  groupNumbers,
  discountGroupIds,
  assortmentId,
  supplierIds,
  query,
  withAgreementPrices = undefined,
  filterDiscontinued,
  fromMainSupplier,
  includeOwner = true,
  inAssortment,
  page,
  size,
  token,
  headers,
}: FetchProps) {
  return fetcher<PaginationResultDTO<MgSupplierItemSearchResultDTO[]>>({
    url: buildUrl({
      base: baseUrl,
      route: mpeApi.mgItems.supplierItems.getFullLinkPath(),
      searchParams: {
        query,
        groupNumbers,
        discountGroupIds,
        assortmentId,
        supplierIds,
        withAgreementPrices,
        filterDiscontinued,
        fromMainSupplier,
        includeOwner,
        inAssortment,
        page,
        size,
      },
    }),
    token,
    headers,
  });
}

export function useMgSupplierItemsSearch(searchProps: SearchOptions, options?: QueryOptions) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();
  const selectedOrgUnitHeader = useSelectedOrgUnitHeader();

  const fetcher = useCallback(
    () =>
      searchMgSupplierItems({
        baseUrl: mpe.url,
        token,
        headers: selectedOrgUnitHeader,
        ...searchProps,
      }),
    [mpe.url, token, selectedOrgUnitHeader, searchProps]
  );
  return useQueryFetch({
    fetcher,
    queryKey: ['mg-supplier-item', 'search', searchProps],
    ...options,
  });
}

export function useMutateMgSupplierItemsSearch(options: QueryOptions = {}) {
  const { mpe } = useMgPrisConfig();
  const { token } = useAuthContext();

  return useMutateMPEFetch<
    PaginationResultDTO<MgSupplierItemSearchResultDTO[]>,
    void,
    SearchOptions
  >({
    url: (searchProps) =>
      buildUrl({
        base: mpe.url,
        route: mpeApi.mgItems.supplierItems.getFullLinkPath(),
        searchParams: { ...searchProps },
      }),
    token,
    method: 'GET',
    mutationKey: ['mg-supplier-item-search'],
    ...options,
  });
}
